import { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendUp, Coin, Bank, Percent, Warning, Clock } from '@phosphor-icons/react';
import { BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { Plus } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [kpis, setKpis] = useState(null);
  const [charts, setCharts] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [kpisRes, chartsRes] = await Promise.all([
        axios.get(`${API}/dashboard/kpis`),
        axios.get(`${API}/dashboard/charts`)
      ]);
      setKpis(kpisRes.data);
      setCharts(chartsRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-muted-foreground">Loading dashboard...</div>
      </div>
    );
  }

  const kpiCards = [
    { label: 'Total Funds Received', value: `$${kpis?.total_funds?.toLocaleString() || 0}`, icon: Coin, color: '#22c55e' },
    { label: 'Total Spent', value: `$${kpis?.total_spent?.toLocaleString() || 0}`, icon: TrendUp, color: '#3b82f6' },
    { label: 'Active Grants', value: kpis?.active_grants || 0, icon: Bank, color: '#E04E39' },
    { label: 'Avg Utilization', value: `${kpis?.avg_utilization || 0}%`, icon: Percent, color: '#f59e0b' },
  ];

  const COLORS = ['#E04E39', '#f59e0b', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

  return (
    <div className="h-full overflow-auto p-6 space-y-6 bg-background text-foreground transition-colors duration-300">
      <div className="border-b border-border pb-4 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-2">
            Leadership Dashboard
          </h1>
          <p className="text-base text-muted-foreground">Real-time operations intelligence and insights</p>
        </div>
        <button
          onClick={() => navigate('/expenses?new=true')}
          className="flex items-center justify-center gap-2 px-6 py-2.5 bg-[#E04E39] hover:bg-[#E86B5A] text-white rounded-md font-bold transition-all shadow-lg hover:shadow-[#E04E39]/20"
        >
          <Plus size={20} weight="bold" />
          NEW EXPENSE
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {kpiCards.map((kpi, idx) => {
          const Icon = kpi.icon;
          return (
            <div 
              key={idx} 
              className="bg-card border border-border rounded-md p-6 hover:-translate-y-1 hover:border-border/50 hover:shadow-lg transition-all"
              data-testid={`kpi-card-${kpi.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="p-2 rounded-md" style={{ backgroundColor: `${kpi.color}15` }}>
                  <Icon size={24} weight="duotone" style={{ color: kpi.color }} />
                </div>
              </div>
              <p className="text-sm text-muted-foreground uppercase tracking-wider mb-2">{kpi.label}</p>
              <p className="text-2xl sm:text-3xl font-semibold">{kpi.value}</p>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        <div className="bg-card border border-border rounded-md p-6">
          <h3 className="text-xl sm:text-2xl font-medium mb-4">Grant Utilization</h3>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={charts?.utilization || []} margin={{ bottom: 100 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-border" />
              <XAxis 
                dataKey="name" 
                stroke="currentColor" 
                className="text-muted-foreground"
                fontSize={10}
                tick={{ angle: -90, textAnchor: 'end', dy: 10 }}
                interval={0}
              />
              <YAxis stroke="currentColor" className="text-muted-foreground" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(0, 0, 0, 0.85)', 
                  border: '1px solid rgba(255, 255, 255, 0.1)', 
                  borderRadius: '8px',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                  backdropFilter: 'blur(4px)'
                }}
                itemStyle={{ color: '#ffffff', fontWeight: 'bold' }}
                labelStyle={{ color: '#E04E39', fontWeight: 'black', marginBottom: '4px' }}
              />
              <Bar 
                dataKey="utilization" 
                fill="#E04E39" 
                radius={[4, 4, 0, 0]} 
                barSize={40}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-card border border-border rounded-md p-6">
          <h3 className="text-xl sm:text-2xl font-medium mb-4">Expense Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={charts?.expense_distribution || []}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry) => `${entry.name}: $${entry.value.toLocaleString()}`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {(charts?.expense_distribution || []).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(0, 0, 0, 0.85)', 
                  border: '1px solid rgba(255, 255, 255, 0.1)', 
                  borderRadius: '8px',
                  backdropFilter: 'blur(4px)'
                }}
                itemStyle={{ color: '#ffffff', fontWeight: 'bold' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-card border border-border rounded-md p-6">
        <h3 className="text-xl sm:text-2xl font-medium mb-4">Monthly Burn Rate</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={charts?.burn_rate || []}>
            <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-border" />
            <XAxis dataKey="month" stroke="currentColor" className="text-muted-foreground" />
            <YAxis stroke="currentColor" className="text-muted-foreground" />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'rgba(0, 0, 0, 0.85)', 
                border: '1px solid rgba(255, 255, 255, 0.1)', 
                borderRadius: '8px',
                backdropFilter: 'blur(4px)'
              }}
              itemStyle={{ color: '#ffffff', fontWeight: 'bold' }}
              labelStyle={{ color: '#22c55e', fontWeight: 'black', marginBottom: '4px' }}
            />
            <Line type="monotone" dataKey="amount" stroke="#22c55e" strokeWidth={2} dot={{ fill: '#22c55e', r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        <div className="bg-card border border-border rounded-md p-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock size={24} weight="duotone" className="text-[#f59e0b]" />
            <h3 className="text-xl sm:text-2xl font-medium">Upcoming Deadlines</h3>
          </div>
          {kpis?.upcoming_deadlines?.length > 0 ? (
            <div className="space-y-3">
              {kpis.upcoming_deadlines.map((deadline, idx) => (
                <div 
                  key={idx} 
                  className="p-3 bg-background border border-border rounded-md"
                  data-testid={`deadline-item-${idx}`}
                >
                  <p className="text-sm font-medium">{deadline.grant_name}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Due in {deadline.days_until} day{deadline.days_until !== 1 ? 's' : ''}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No upcoming deadlines</p>
          )}
        </div>

        <div className="bg-card border border-border rounded-md p-6">
          <div className="flex items-center gap-2 mb-4">
            <Warning size={24} weight="duotone" className="text-[#ef4444]" />
            <h3 className="text-xl sm:text-2xl font-medium">Risk Panel</h3>
          </div>
          <div className="space-y-4">
            <div className="p-4 bg-[#ef4444]/10 border border-[#ef4444]/30 rounded-md">
              <p className="text-sm font-medium mb-1">Grants at Risk</p>
              <p className="text-2xl font-bold text-[#ef4444]" data-testid="grants-at-risk-count">
                {kpis?.grants_at_risk || 0}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Under/over utilization or missing reports</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
